// The guided 3-step submit flow: Category → Details → Confirm & Send, plus
// the receipt. One decision per screen — built for a stressed agent on a
// phone: big tap targets, minimal typing (the title is auto-composed),
// GPS + hierarchy auto-tagged.
import { type CSSProperties, useEffect, useState } from 'react';

import { ISOBSERVER } from '../../../constants/global';
import { useFormStage } from '../../../hooks/useFormStage';
import useGeolocation from '../../../hooks/useGeolocation';
import {
	INCIDENT_CATEGORIES,
	INCIDENT_SEVERITIES,
	type IncidentCategory,
	type IncidentSeverity,
	MAX_INCIDENT_DESCRIPTION,
	MAX_INCIDENT_PHOTOS,
	MAX_INCIDENT_TITLE,
} from '../../../interface/vesr';
import { TypeButton } from '../../utils/button';
import { useCopy } from '../../utils/hooks';
import { TypeInput } from '../../utils/input';
import { fileKey, MultipleMediaUploadComponent } from '../../utils/multiple-upload-document';
import { BackArrowSVG, CheckSVG } from '../../utils/svgs';
import { TypeTextArea } from '../../utils/text-area';
import { VideoEvidenceField } from '../../utils/video-evidence';
import { CategoryGlyph, PinGlyph } from './icons';
import { SeverityChip } from './shared';
import { useIncidentSubmit } from './submit-machine';
import { buildCreateIncidentRequest, composeTitle, contextRows, type IReportContext } from './utils';

type NewReportStage = 'category' | 'details' | 'confirm';

interface INewReportProps {
	ctx: IReportContext;
	onTrackReport: () => void;
	/** INEC has cancelled the agent's own unit — Security/Electoral reports may belong on that report. */
	cancellationActive?: boolean;
	onAddToCancellation?: () => void;
}

/** Categories that, at a cancelled unit, are most likely the cancellation itself. */
const CANCELLATION_LIKE: readonly IncidentCategory[] = ['Security', 'Electoral'];

const vibrate = (ms: number) => {
	try {
		navigator.vibrate?.(ms);
	} catch {
		// Unsupported — progressive enhancement only.
	}
};

const NewReport = ({ ctx, onTrackReport, cancellationActive, onAddToCancellation }: INewReportProps) => {
	const { stage, navigator: nav, isStageActive } = useFormStage<NewReportStage>('category', ['category', 'details', 'confirm']);

	const [category, setCategory] = useState<IncidentCategory | null>(null);
	// A Security/Electoral pick at a cancelled unit waits here while the agent chooses where it goes.
	const [divertFrom, setDivertFrom] = useState<IncidentCategory | null>(null);
	const [severity, setSeverity] = useState<IncidentSeverity>('Medium');
	const [note, setNote] = useState('');
	const [files, setFiles] = useState<File[]>([]);
	const [video, setVideo] = useState<File | null>(null);
	const [title, setTitle] = useState('');
	const [titleTouched, setTitleTouched] = useState(false);

	const geo = useGeolocation();
	const hasCoords = geo.latitude != null && geo.longitude != null;

	const machine = useIncidentSubmit();
	const { copySuccess, copy } = useCopy();

	// 429 countdown for the Send button.
	const [cooldown, setCooldown] = useState(0);
	useEffect(() => {
		const retryAfter = machine.createError?.retryAfterSeconds;
		if (!retryAfter) return;
		setCooldown(retryAfter);
		const timer = setInterval(() => {
			setCooldown((s) => {
				if (s <= 1) {
					clearInterval(timer);
					return 0;
				}
				return s - 1;
			});
		}, 1000);
		return () => clearInterval(timer);
	}, [machine.createError]);

	// Keep the auto-composed title fresh until the agent edits it by hand.
	useEffect(() => {
		if (!titleTouched && category) setTitle(composeTitle(category, severity, note));
	}, [category, severity, note, titleTouched]);

	const selectCategory = (value: IncidentCategory, force = false) => {
		if (!force && cancellationActive && onAddToCancellation && CANCELLATION_LIKE.includes(value)) {
			setDivertFrom(value);
			return;
		}
		setDivertFrom(null);
		setCategory(value);
		vibrate(10);
		nav.next();
	};

	const submitting = machine.phase === 'creating' || machine.phase === 'uploading';
	// An observer's report belongs to their posting: without one it would carry no election and
	// no place, and their organisation's desk could not place it.
	const needsPosting = ISOBSERVER() && !ctx.electionId;
	const canSend = !!category && !!title.trim() && hasCoords && !submitting && cooldown === 0 && !needsPosting;

	const onSend = () => {
		if (!canSend || !category || geo.latitude == null || geo.longitude == null) return;
		vibrate(15);
		void machine.submit({
			request: buildCreateIncidentRequest(ctx, {
				title,
				note,
				category,
				severity,
				latitude: geo.latitude,
				longitude: geo.longitude,
			}),
			files,
			video,
			coords: { latitude: geo.latitude, longitude: geo.longitude, accuracy: geo.accuracy ?? 0 },
			ctx,
			description: note,
		});
	};

	const fileAnother = () => {
		machine.reset();
		setCategory(null);
		setSeverity('Medium');
		setNote('');
		setFiles([]);
		setVideo(null);
		setTitle('');
		setTitleTouched(false);
		nav.goTo('category')();
	};

	const shareReceipt = async () => {
		const referenceId = machine.incident?.referenceId;
		if (!referenceId) return;
		const text = `Situation report #${referenceId} filed via iElect — ${title}`;
		if (navigator.share) {
			try {
				await navigator.share({ title: 'Situation report', text });
				return;
			} catch {
				// Cancelled / unsupported — fall through to copy.
			}
		}
		copy(text);
	};

	// -------------------------------------------------------------- Receipt
	if (machine.phase === 'done' && machine.incident) {
		const failedCount = machine.failedFiles.length;
		const rejectedCount = machine.rejectedFiles.length;
		return (
			<div className='rh-receipt animate-fade-in'>
				<span className='rh-receipt__icon'>
					<CheckSVG
						width='30'
						height='30'
						color='var(--success-600)'
					/>
				</span>
				<h3 className='rh-receipt__title'>Report filed</h3>
				<p className='rh-receipt__hint'>The situation room has your report. Track its status in My Reports.</p>
				<div className='rh-receipt__ref-wrap'>
					<code className='rh-receipt__ref'>#{machine.incident.referenceId}</code>
					<div className='rh-receipt__ref-actions'>
						<button
							type='button'
							className='rh-receipt__ref-btn'
							onClick={() => copy(machine.incident?.referenceId || '')}
						>
							{copySuccess ? '✓ Copied' : 'Copy'}
						</button>
						<button
							type='button'
							className='rh-receipt__ref-btn'
							onClick={shareReceipt}
						>
							Share
						</button>
					</div>
				</div>
				<div className='rh-receipt__recap'>
					{category ? (
						<span
							className='rh-recap-pill'
							style={{ '--accent': INCIDENT_CATEGORIES.find((c) => c.value === category)?.accent } as CSSProperties}
						>
							<CategoryGlyph category={category} />
							{category}
						</span>
					) : null}
					<SeverityChip severity={severity} />
				</div>
				{failedCount ? (
					<div className='rh-banner rh-banner--warning'>
						<p className='rh-banner__text'>
							{failedCount} of {files.length + (video ? 1 : 0)} file{files.length + (video ? 1 : 0) > 1 ? 's' : ''} didn’t upload.
							Your report is safe — retry just the files.
						</p>
						<TypeButton
							title='Retry uploads'
							buttonSize='small'
							buttonType='outlined'
							onClick={() => void machine.retryPhotos()}
						/>
					</div>
				) : null}
				{/* Too large is terminal — offering "retry" here would re-send the same
				    bytes into the same cap. Name the fix instead: use a smaller file. */}
				{rejectedCount ? (
					<div className='rh-banner rh-banner--warning'>
						<p className='rh-banner__text'>
							{rejectedCount === 1 ? 'One file was' : `${rejectedCount} files were`} too large for the situation room, so{' '}
							{rejectedCount === 1 ? 'it wasn’t' : 'they weren’t'} attached. Your report is filed — add a shorter or
							smaller-resolution clip from My Reports.
						</p>
					</div>
				) : null}
				<div className='rh-receipt__ctas'>
					<TypeButton
						title='Track this report'
						buttonSize='small'
						className='w-100'
						onClick={onTrackReport}
					/>
					<TypeButton
						title='File another'
						buttonSize='small'
						buttonType='outlined'
						className='w-100'
						onClick={fileAnother}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className='rh-new'>
			{/* Slim progress + back */}
			<div className='rh-new__top'>
				{!isStageActive('category') ? (
					<button
						type='button'
						className='rh-new__back'
						onClick={nav.prev}
					>
						<BackArrowSVG />
						<span>Back</span>
					</button>
				) : (
					<span />
				)}
				<div
					className='rh-new__progress'
					aria-label={`Step ${stage === 'category' ? 1 : stage === 'details' ? 2 : 3} of 3`}
				>
					{(['category', 'details', 'confirm'] as NewReportStage[]).map((s, i) => (
						<span
							key={s}
							className={`rh-new__progress-seg ${
								stage === s ? 'is-current' : i < ['category', 'details', 'confirm'].indexOf(stage) ? 'is-done' : ''
							}`}
						/>
					))}
				</div>
			</div>

			{/* Step 1 — Category */}
			{isStageActive('category') ? (
				<div className='rh-step'>
					<h4 className='rh-step__title'>What’s happening?</h4>
					<p className='rh-step__hint'>Pick the closest match — details come next.</p>
					<div
						className='rh-cat-grid stagger-children'
						role='radiogroup'
						aria-label='Report category'
					>
						{INCIDENT_CATEGORIES.map((cat) => (
							<div
								key={cat.value}
								className='rh-cat-card'
								style={{ '--accent': cat.accent } as CSSProperties}
								role='radio'
								aria-checked={category === cat.value}
								tabIndex={0}
								onClick={() => selectCategory(cat.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										selectCategory(cat.value);
									}
								}}
							>
								<span
									className='rh-cat-card__icon'
									aria-hidden='true'
								>
									<CategoryGlyph category={cat.value} />
								</span>
								<p className='rh-cat-card__title'>{cat.label}</p>
								<p className='rh-cat-card__desc'>{cat.helper}</p>
							</div>
						))}
					</div>
					{divertFrom ? (
						<div
							className='rh-divert'
							role='group'
							aria-labelledby='rh-divert-q'
						>
							<p
								id='rh-divert-q'
								className='rh-divert__q'
							>
								Add to your cancellation report instead?
							</p>
							<p className='rh-divert__hint'>
								INEC cancelled your unit. Photos and notes about it reach your party fastest on that report.
							</p>
							<div className='rh-divert__actions'>
								<button
									type='button'
									className='rh-divert__btn rh-divert__btn--primary'
									onClick={() => {
										setDivertFrom(null);
										onAddToCancellation?.();
									}}
								>
									Add evidence
								</button>
								<button
									type='button'
									className='rh-divert__btn'
									onClick={() => selectCategory(divertFrom, true)}
								>
									Continue new report
								</button>
							</div>
						</div>
					) : null}
				</div>
			) : null}

			{/* Step 2 — Severity + photos + note */}
			{isStageActive('details') ? (
				<div className='rh-step'>
					<h4 className='rh-step__title'>How serious is it?</h4>
					<div
						className='rh-sev-ramp'
						role='radiogroup'
						aria-label='Severity'
					>
						{INCIDENT_SEVERITIES.map((sev) => (
							<button
								key={sev.value}
								type='button'
								role='radio'
								aria-checked={severity === sev.value}
								className={`rh-sev-pill rh-sev-pill--${sev.value.toLowerCase()} ${severity === sev.value ? 'is-selected' : ''}`}
								style={{ '--sev': sev.color, '--sev-bg': sev.bg } as CSSProperties}
								onClick={() => {
									setSeverity(sev.value);
									vibrate(10);
								}}
							>
								<span className='rh-sev-pill__label'>{sev.label}</span>
								<span className='rh-sev-pill__helper'>{sev.helper}</span>
							</button>
						))}
					</div>

					<div className='rh-field'>
						<p className='rh-field__label'>Add evidence (optional)</p>
						<MultipleMediaUploadComponent
							setFiles={(next) => setFiles(next.slice(0, MAX_INCIDENT_PHOTOS))}
							files={files}
							uploadedFiles={[]}
							captureTitle='Add evidence'
							captureSubtitle='Snap what you’re seeing right now'
						/>
						{files.length >= MAX_INCIDENT_PHOTOS ? <p className='rh-field__hint'>10 photos max per report.</p> : null}
						<VideoEvidenceField
							video={video}
							setVideo={setVideo}
						/>
					</div>

					<div className='rh-field rh-field--note'>
						<TypeTextArea
							label='Add a note (optional)'
							placeholder='What did you see? Who’s involved? Anything that helps the situation room act fast…'
							value={note}
							onChange={(e) => {
								if (e.target.value.length > MAX_INCIDENT_DESCRIPTION) return;
								setNote(e.target.value);
							}}
						/>
						<span className='rh-field__count'>
							{note.length}/{MAX_INCIDENT_DESCRIPTION}
						</span>
					</div>

					<div className='rh-sticky-cta'>
						<TypeButton
							title='Continue'
							className='w-100'
							buttonSize='small'
							onClick={nav.next}
						/>
					</div>
				</div>
			) : null}

			{/* Step 3 — Confirm & send */}
			{isStageActive('confirm') ? (
				<div className='rh-step'>
					<h4 className='rh-step__title'>Confirm & send</h4>

					{category ? (
						<div className='rh-confirm-recap'>
							<span
								className='rh-recap-pill'
								style={{ '--accent': INCIDENT_CATEGORIES.find((c) => c.value === category)?.accent } as CSSProperties}
							>
								<CategoryGlyph category={category} />
								{category}
							</span>
							<SeverityChip severity={severity} />
							{files.length ? <span className='rh-recap-count'>{files.length} 📷</span> : null}
							{video ? <span className='rh-recap-count'>1 🎥</span> : null}
						</div>
					) : null}

					<TypeInput
						label='Report title'
						value={title}
						maxLength={MAX_INCIDENT_TITLE}
						onChange={(e) => {
							setTitleTouched(true);
							setTitle(e.target.value);
						}}
					/>

					{/* GPS chip */}
					<div className={`rh-gps ${hasCoords ? 'rh-gps--ok' : geo.error ? 'rh-gps--error' : 'rh-gps--pending'}`}>
						<PinGlyph />
						<div className='rh-gps__text'>
							{geo.loading ? (
								<span>Capturing your location…</span>
							) : hasCoords ? (
								<>
									<span>
										Location locked · {geo.latitude!.toFixed(5)}, {geo.longitude!.toFixed(5)}
									</span>
									<span className='rh-gps__sub'>These coordinates are sent with your report.</span>
								</>
							) : (
								<span>{geo.error || 'Location unavailable.'}</span>
							)}
						</div>
						{!geo.loading && !hasCoords ? (
							<button
								type='button'
								className='rh-gps__retry'
								onClick={geo.retry}
							>
								Retry
							</button>
						) : null}
					</div>

					{/* Posting context */}
					{contextRows(ctx).length ? (
						<div className='rh-context'>
							<span className='rh-context__eyebrow'>{ctx.level || 'Your assignment'}</span>
							<div className='rh-context__rows'>
								{contextRows(ctx).map((row) => (
									<span
										className='rh-context__row'
										key={row.label}
									>
										<span className='rh-context__row-label'>{row.label}</span>
										<span className='rh-context__row-value'>{row.value}</span>
									</span>
								))}
							</div>
						</div>
					) : null}

					{/* Media upload progress (during send) */}
					{machine.phase === 'uploading' || Object.keys(machine.fileStatuses).length ? (
						<>
							{files.length ? (
								<MultipleMediaUploadComponent
									setFiles={setFiles}
									files={files}
									uploadedFiles={[]}
									load={machine.phase === 'uploading'}
									fileStatuses={machine.fileStatuses}
								/>
							) : null}
							{video ? (
								<VideoEvidenceField
									video={video}
									setVideo={setVideo}
									status={machine.fileStatuses[fileKey(video)]}
									onRetry={() => void machine.retryPhotos()}
									disabled={submitting}
								/>
							) : null}
						</>
					) : null}

					{machine.createError ? (
						<div className={`rh-banner ${machine.createError.offline ? 'rh-banner--offline' : 'rh-banner--warning'}`}>
							<p className='rh-banner__text'>
								{machine.createError.message}
								{cooldown > 0 ? ` Retry in ${cooldown}s.` : ''}
							</p>
						</div>
					) : null}

					<div className='rh-sticky-cta'>
						{/* Always submits fresh args; the idempotency key persists across
						    retries inside the machine, so a retried create never duplicates. */}
						<TypeButton
							title={cooldown > 0 ? `Retry in ${cooldown}s` : machine.createError && !submitting ? 'Retry send' : 'Send report'}
							className='w-100'
							buttonSize='small'
							buttonType={canSend ? 'bold' : 'disabled'}
							load={submitting}
							onClick={onSend}
						/>
						{!hasCoords && !geo.loading ? (
							<p className='rh-field__hint'>Location access is required to file a situation report.</p>
						) : null}
						{needsPosting ? (
							<p className='rh-field__hint'>You can send reports once your observer desk assigns you a polling unit.</p>
						) : null}
					</div>
				</div>
			) : null}
		</div>
	);
};

export default NewReport;
